'use client'

import { cn } from '@/lib/utils'
import { useToast } from '@/providers/ToastProvider'
import { X } from 'lucide-react'

interface ToastProps {
  id: string
  title: string
  description?: string
  variant?: 'default' | 'destructive' | 'success'
  onDismiss: (id: string) => void
}

export function Toast({ id, title, description, variant = 'default', onDismiss }: ToastProps) {
  const variantStyles = {
    default: 'bg-white border-gray-300 text-gray-900 shadow-xl',
    destructive: 'bg-red-500 border-red-500 text-white shadow-xl',
    success: 'bg-green-500 border-green-500 text-white shadow-xl'
  }

  const iconStyles = {
    default: 'text-gray-400 hover:text-gray-600',
    destructive: 'text-white/80 hover:text-white',
    success: 'text-white/80 hover:text-white'
  }

  return (
    <div
      className={cn(
        'relative w-full max-w-sm p-4 border-2 rounded-xl shadow-2xl transition-all duration-300 ease-in-out transform hover:scale-105',
        'animate-in slide-in-from-right-full duration-300',
        variant === 'success' && 'animate-pulse',
        variantStyles[variant]
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h4 className="font-semibold text-base">{title}</h4>
          {description && (
            <p className="text-sm mt-1 opacity-90">{description}</p>
          )}
        </div>
        <button
          onClick={() => onDismiss(id)}
          className={cn(
            'ml-4 p-1.5 rounded-full hover:bg-black/10 transition-colors',
            iconStyles[variant]
          )}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

interface ToastContainerProps {
  toasts: Array<{
    id: string
    title: string
    description?: string
    variant?: 'default' | 'destructive' | 'success'
  }>
  onDismiss: (id: string) => void
}

export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  if (toasts.length === 0) return null

  return (
    <div className="fixed top-6 right-6 z-[9999] space-y-3 pointer-events-none">
      {toasts.map((toast, index) => (
        <div
          key={toast.id}
          className="pointer-events-auto"
          style={{
            animationDelay: `${index * 100}ms`
          }}
        >
          <Toast
            {...toast}
            onDismiss={onDismiss}
          />
        </div>
      ))}
    </div>
  )
}

export function Toaster() {
  const { toasts, dismiss } = useToast()
  
  return <ToastContainer toasts={toasts} onDismiss={dismiss} />
}
